import { type FC, useState, useCallback, useMemo } from "react";
import { Pencil } from "lucide-react";

import { Button } from "@/shared/components/ui/button";

import type { FormField, FormFieldTooltip } from "@/shared/types/estateEssentials";

import {
  getGridClass,
  getRevealedDisplayValue,
  groupFieldsByRow,
  resolveFieldLabel,
} from "../utils/formDisplayUtils";
import { FieldLabel, FieldTooltip } from "./FieldGrid";
import { FormFieldInput } from "./FormFieldInput";
import { SectionNumber } from "./SectionHeader";

interface ReviewDisplayItem {
  key: string;
  label: string;
  value: string;
  tooltip?: FormFieldTooltip;
}

function buildReviewDisplayItems(
  fields: FormField[],
  values: Record<string, string>,
  deceasedFirstName?: string,
): ReviewDisplayItem[] {
  const items: ReviewDisplayItem[] = [];
  const processedCombineKeys = new Set<string>();

  for (const field of fields) {
    if (field.reviewCombine) {
      if (processedCombineKeys.has(field.reviewCombine)) continue;
      processedCombineKeys.add(field.reviewCombine);

      const groupFields = fields.filter((f) => f.reviewCombine === field.reviewCombine);
      const labelSource = groupFields.find((f) => f.reviewLabel);
      let label = labelSource?.reviewLabel ?? field.label;
      if (label === "address" && deceasedFirstName) {
        label = `${deceasedFirstName}'s address`;
      } else if (label === "mailing_address") {
        label = "Current mailing address";
      }

      const parts = groupFields.map((f) => values[f.key]?.trim() ?? "").filter(Boolean);
      items.push({
        key: field.reviewCombine,
        label,
        value: parts.length > 0 ? parts.join(", ") : "-",
      });
    } else {
      const label = resolveFieldLabel(field, deceasedFirstName);
      items.push({
        key: field.key,
        label,
        value: getRevealedDisplayValue(field, values[field.key] ?? ""),
        tooltip: field.tooltip,
      });
    }
  }

  return items;
}

interface ReviewSectionProps {
  sectionNumber?: number;
  sectionTitle?: string;
  fields: FormField[];
  values: Record<string, string>;
  onFieldChange: (key: string, value: string) => void;
  errors?: Record<string, string>;
  deceasedFirstName?: string;
}

export const ReviewSection: FC<ReviewSectionProps> = ({
  sectionNumber,
  sectionTitle,
  fields,
  values,
  onFieldChange,
  errors,
  deceasedFirstName,
}) => {
  const [isEditing, setIsEditing] = useState(false);

  const handleEditToggle = useCallback(() => {
    setIsEditing((prev) => !prev);
  }, []);

  const groups = groupFieldsByRow(fields);

  const displayItems = useMemo(
    () => buildReviewDisplayItems(fields, values, deceasedFirstName),
    [fields, values, deceasedFirstName],
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-2 text-base font-medium text-primary">
          {sectionNumber !== undefined && <SectionNumber n={sectionNumber} />}
          {sectionTitle ?? "Review information"}
        </p>
        <Button variant="link" size="sm" onClick={handleEditToggle} className="h-auto p-0 text-sm">
          {isEditing ? (
            "Done"
          ) : (
            <span className="flex items-center gap-1">
              <Pencil className="h-3.5 w-3.5" />
              Edit
            </span>
          )}
        </Button>
      </div>

      <div className="rounded-lg border border-primary bg-white p-6">
        {isEditing ? (
          <div className="space-y-3">
            {groups.map((group) => (
              <div key={group[0].key} className={getGridClass(group.length)}>
                {group.map((field) => {
                  const label = resolveFieldLabel(field, deceasedFirstName);
                  return (
                    <div key={field.key} className="space-y-1">
                      <FieldLabel field={{ ...field, label }} />
                      <FormFieldInput
                        field={{ ...field, label }}
                        value={values[field.key] ?? ""}
                        onFieldChange={onFieldChange}
                        fieldKey={field.key}
                        error={!!errors?.[field.key]}
                      />
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
            {displayItems.map((item) => (
              <div key={item.key} className="space-y-1">
                <p className="flex items-center gap-1 text-sm font-semibold text-primary">
                  {item.label}
                  {item.tooltip && <FieldTooltip tooltip={item.tooltip} />}
                </p>
                <p className="text-secondary leading-relaxed">{item.value}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
